import { z } from "zod";
import { managerColorSchema } from "./colors";
import { boardQuerySchema } from "./opportunities";

/**
 * Analytics — the pipeline's summary strip and its Detailed view. See
 * specs/analytics-strip-redesign.md.
 *
 * **Analytics holds no data of its own.** Every figure below is computed at read time from
 * the opportunities that pass the active filters, in SQL over `opportunity_derived`, and
 * nothing here is stored or cached. This file types those figures; it never calculates one.
 *
 * The query *is* `boardQuerySchema`, deliberately: the strip renders under the pipeline
 * toolbar and **must** be filtered exactly as the board beneath it is. Sharing the board's
 * own schema is what makes that structural rather than a promise — a filter added to the
 * board cannot be forgotten here, and a filter *removed* from it cannot survive here.
 *
 * There is no scope any more. The standalone Analytics screen and its Mine / Organisation
 * toggle are gone (2026-08-25): "mine" is the owner filter, which multi-select states
 * exactly, and two mechanisms for one narrowing is one too many.
 */

/** The one analytics query — the board's, unchanged and unextended. */
export const analyticsQuerySchema = boardQuerySchema;
export type AnalyticsQuery = z.infer<typeof analyticsQuerySchema>;

/** Enough of an opportunity to name it and open it. Never a full record. */
export const analyticsOpportunityRefSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** Where clicking goes — the record's URL identity, derived server-side. */
  slug: z.string(),
  clientName: z.string(),
  /** Decimal string, never a float. */
  nip: z.string(),
  stageId: z.string().uuid(),
  stageName: z.string(),
  ownerId: z.string().uuid(),
  ownerName: z.string(),
});
export type AnalyticsOpportunityRef = z.infer<typeof analyticsOpportunityRefSchema>;

/**
 * The five metrics, whose definitions live in the book's Analytics and Summary Strip page.
 *
 * Every one of them is **open only** — closed opportunities leave operational pipeline
 * totals, because pipeline means what is still live — and deleted opportunities are absent
 * from all of them.
 */
export const analyticsMetricsSchema = z.object({
  /** Count of opportunities whose status is Open. */
  openCount: z.number().int(),
  /** Sum of NIP across open opportunities. */
  pipelineValue: z.string(),
  /**
   * Closing this quarter — and the period it means, so the label can name it.
   *
   * Decision Log row 24: the tile follows the **displayed year**, carrying the
   * calendar-current quarter number (in `APP_TIMEZONE`) applied to the scoped year. A tile
   * that read zero on a 2027 board while cards sat in its columns would be the exact
   * mistrust this product exists to remove.
   */
  closingThisQuarter: z.object({
    quarter: z.number().int().min(1).max(4),
    year: z.number().int(),
    value: z.string(),
    count: z.number().int(),
  }),
  /** Open opportunities at or beyond the stalled threshold, read from the view. */
  stalledCount: z.number().int(),
  /** The highest-value open opportunity, or nothing when there is none. */
  largest: analyticsOpportunityRefSchema.nullable(),
});
export type AnalyticsMetrics = z.infer<typeof analyticsMetricsSchema>;

/**
 * One bar of the pipeline distribution — the Detailed view's **By phase** tab.
 *
 * Value-scaled with the count printed on the bar (row 25), in stage order, and explicitly
 * **not** a funnel (row 38) — a funnel asserts conversion rates v1.0 does not calculate.
 * Every active open stage gets a row, including the empty ones: a stage holding nothing is
 * information, and a missing row reads as a missing stage.
 */
export const analyticsDistributionRowSchema = z.object({
  stageId: z.string().uuid(),
  stageName: z.string(),
  displayOrder: z.number().int(),
  count: z.number().int(),
  totalNip: z.string(),
});
export type AnalyticsDistributionRow = z.infer<typeof analyticsDistributionRowSchema>;

/**
 * One bar of the Detailed view's **By manager** tab — computed for every reader, whatever
 * their role.
 *
 * This replaced the Head-of-Sales-only management block, and the reason is the product's
 * own: the board is shared, every manager already sees every card *and* its owner, so a
 * breakdown by owner discloses nothing a reader could not add up by hand. Role-gating it
 * bought secrecy nobody had.
 *
 * `color` is the owner's `--manager-*` key, or `null` where none is assigned — the bar is
 * tinted from the one ownership-colour system the cards use rather than a second palette.
 * `stalledNip` is the part of `totalNip` sitting at or beyond the stalled threshold, taken
 * from the derivation view like every other stalled classification. It travels as its own
 * figure because the bar renders it as a segment *and* prints it: a segment distinguished
 * by colour alone would say nothing to a colour-blind reader.
 */
export const byManagerRowSchema = z.object({
  ownerId: z.string().uuid(),
  ownerName: z.string(),
  color: managerColorSchema.nullable(),
  count: z.number().int(),
  /** Decimal strings — money is added up by Postgres in `numeric`, never in JavaScript. */
  totalNip: z.string(),
  stalledNip: z.string(),
});
export type ByManagerRow = z.infer<typeof byManagerRowSchema>;

/**
 * One response per render.
 *
 * Everything the strip and its Detailed view show arrives together, from one snapshot, so no
 * two figures on screen can come from different instants — which is the whole reason this is
 * one endpoint rather than four. The response mirrors what renders: `topOpportunities`, the
 * `management` block and `scope` are gone with the screen that drew them, because a block
 * nothing draws is payload and SQL for nothing.
 */
export const analyticsResponseSchema = z.object({
  /** The year the figures cover — derived server-side, like the board's. */
  year: z.number().int(),
  metrics: analyticsMetricsSchema,
  distribution: z.array(analyticsDistributionRowSchema),
  byManager: z.array(byManagerRowSchema),
});
export type AnalyticsResponse = z.infer<typeof analyticsResponseSchema>;
